import { Controller } from "@hotwired/stimulus";
import L from "leaflet";
import {
  clearLeafletArtifacts,
  containerHasSize,
  watchContainerSize,
} from "./utils/map_container";

/**
 * Locating Map Controller (Restaurant Audit, Step 1)
 *
 * Drives the live, "discovering competitors in real
 * time" map for the locating step.
 *
 * Architecture (CAL-264). The locating canvas is
 * rendered ONCE on page load and never swapped by
 * `audit-stage`. All state is value-driven from
 * Stimulus values that the server updates via
 * `set_dataset_attributes` Turbo Stream actions.
 *
 * Lifecycle:
 *
 * 1. **connect()**: init the Leaflet map. Center on
 *    the venue if we already have geo, otherwise on
 *    the default Mexican metro (CDMX). If we already
 *    have geo at connect (page loaded into a
 *    completed audit), kick off the staggered
 *    reveal immediately.
 *
 * 2. **`hasGeoPayloadValueChanged`**: fires when
 *    narration arrives via dataset broadcast. We pan
 *    to the venue, update the header copy and stats
 *    strip from the new values, then start the
 *    staggered reveal.
 *
 * 3. **Staggered reveal**: drop venue pin, then
 *    each competitor pin every COMPETITOR_STAGGER_MS
 *    while flying the map to fit the new bounds.
 *    Counter target increments per pin. Dispatches
 *    `locating-map:complete` when the last pin
 *    lands so `audit-stage` can advance.
 *
 * The map is interaction-locked (no zoom, drag,
 * scroll, keyboard): it's a backdrop, not a tool.
 */

// Per-pin reveal cadence. Long enough that each
// landing reads as "Calisto found another one"
// rather than blurring together into a single
// wave; short enough that the user isn't waiting
// on five drops to advance.
const COMPETITOR_STAGGER_MS = 1100;

// Delay between the venue pin landing and the
// first competitor showing up. Gives the focal
// pin a beat of its own before the supporting
// cast arrives.
const FIRST_COMPETITOR_DELAY_MS = 900;
const FIT_PADDING = [80, 80];
const FIT_PADDING_BOTTOM = 180;

export default class extends Controller {
  static values = {
    narrated: { type: String, default: "false" },
    venueName: { type: String, default: "" },
    city: { type: String, default: "" },
    rating: { type: String, default: "" },
    reviewsCount: { type: String, default: "" },
    cuisine: { type: String, default: "" },
    cuisineMeta: { type: String, default: "" },
    neighborsMeta: { type: String, default: "" },
    website: { type: String, default: "" },
    websiteHost: { type: String, default: "" },
    venueLat: { type: String, default: "" },
    venueLng: { type: String, default: "" },
    competitors: { type: Array, default: [] },
    competitorPinHtml: {
      type: Array,
      default: [],
    },
    venuePinHtml: { type: String, default: "" },
    tilesUrl: String,
    defaultLat: Number,
    defaultLng: Number,
    defaultZoom: Number,
    hasGeoPayload: {
      type: String,
      default: "false",
    },
  };

  static targets = [
    "map",
    "narration",
    "ratingValue",
    "ratingMeta",
    "cuisineValue",
    "cuisineMeta",
    "neighborsCount",
    "neighborsMeta",
    "websiteValue",
    "websiteMeta",
  ];

  connect() {
    // `reconnect_controllers.js` re-runs the lifecycle by
    // hand after a morph, on top of Stimulus's own, so
    // connect() can run twice with no disconnect in
    // between. And this one RESET `mapInitialized` on
    // the way in, so the guard could not stop the second
    // `L.map()` from meeting a container Leaflet had
    // already stamped. Tear down first instead; the guard
    // then means "already built during THIS connect",
    // which is what it was for.
    this.teardownMap();

    this.competitorTimeouts = [];
    this.revealStarted = false;
    this.revealedCompetitors = 0;
    this.reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    this.initMap();

    if (this.geoReady()) {
      // Page loaded into an already-narrated audit
      // OR a value-changed callback fired before
      // connect() and stashed the new state without
      // being able to act on it (no `this.map` yet).
      // Sync the header + stats from values (the
      // server already did this on first paint, but
      // the same code path covers post-broadcast too)
      // and play the staggered reveal.
      this.syncHeader();
      this.syncStats();
      this.beginReveal();
    }

    setTimeout(() => {
      if (this.map) this.map.invalidateSize();
    }, 100);

    this.watchContainer();
  }

  // Every audit step visual is painted up front under
  // `display: none` and revealed when `audit-stage` flips
  // it current, so this map is routinely built with no
  // box at all. Leaflet only re-measures on a WINDOW
  // resize, so without this it keeps painting at 0x0
  // after the stage opens (CAL-445).
  watchContainer() {
    this.resizeObserver = watchContainerSize(
      this.mapTarget,
      () => this.handleContainerResize()
    );
  }

  handleContainerResize() {
    if (!this.map) return;

    this.map.invalidateSize();

    if (!this.viewComputedBlind) return;

    this.viewComputedBlind = false;

    // A plain `setView` survives a 0x0 container intact,
    // so only a FITTED view needs redoing, and only once
    // the geocode has landed, because `refitVisibleBounds`
    // reads the venue off values that are empty until
    // then (empty coerces to 0, which is the Gulf of
    // Guinea, not a venue).
    if (this.geoReady()) {
      this.refitVisibleBounds();
    }
  }

  disconnect() {
    this.teardownMap();
  }

  // Returns the map target to a state `L.map()` accepts,
  // and the controller to a state a fresh connect can
  // start from. `map.remove()` is the happy path, but it
  // throws ("Map container is being reused by another
  // instance") when the target no longer carries the id
  // it stamped, and it throws BEFORE cleaning anything
  // up. Swallow it and let the sweep finish, or the next
  // connect meets a stamped container and the audit's
  // first step shows a dead map for the whole session.
  teardownMap() {
    this.clearCompetitorTimeouts();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.viewComputedBlind = false;

    if (this.map) {
      try {
        this.map.remove();
      } catch {
        // Half-dead map: the sweep below still frees
        // the container for a re-init.
      }
    }

    this.map = null;
    this.mapInitialized = false;

    if (this.hasMapTarget) {
      clearLeafletArtifacts(this.mapTarget);
    }
  }

  // -------- value-changed reactors --------

  hasGeoPayloadValueChanged(value, previousValue) {
    // Stimulus fires every value-changed callback
    // once on connect with previousValue=undefined.
    // Skip that: the connect() flow handles initial
    // state.
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    if (!this.geoReady()) return;

    this.recenterOnVenue();
    this.syncHeader();
    this.syncStats();
    this.beginReveal();
  }

  narratedValueChanged(value, previousValue) {
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.syncHeader();
    this.syncStats();
  }

  // -------- map init --------

  initMap() {
    if (this.mapInitialized) return;
    this.mapInitialized = true;

    this.map = L.map(this.mapTarget, {
      zoomControl: false,
      scrollWheelZoom: false,
      dragging: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
      attributionControl: true,
      fadeAnimation: false,
      zoomAnimation: !this.reducedMotion,
    });

    L.tileLayer(this.tilesUrlValue, {
      attribution:
        "&copy; OpenStreetMap, &copy; CARTO",
      maxZoom: 18,
    }).addTo(this.map);

    this.viewComputedBlind = !containerHasSize(
      this.mapTarget
    );

    this.map.setView(
      this.initialCenter(),
      this.initialZoom()
    );
  }

  initialCenter() {
    if (this.geoReady()) {
      return [
        Number(this.venueLatValue),
        Number(this.venueLngValue),
      ];
    }
    return [
      this.defaultLatValue,
      this.defaultLngValue,
    ];
  }

  initialZoom() {
    return this.geoReady()
      ? 14
      : this.defaultZoomValue;
  }

  recenterOnVenue() {
    if (!this.map) return;
    if (!this.geoReady()) return;
    const point = [
      Number(this.venueLatValue),
      Number(this.venueLngValue),
    ];
    if (this.reducedMotion) {
      this.map.setView(point, 14, { animate: false });
    } else {
      this.map.flyTo(point, 14, {
        animate: true,
        duration: 0.5,
      });
    }
  }

  geoReady() {
    return (
      this.hasGeoPayloadValue === "true" &&
      this.venueLatValue !== "" &&
      this.venueLngValue !== ""
    );
  }

  // -------- header / stats sync from values --------

  syncHeader() {
    if (!this.hasNarrationTarget) return;
    if (this.narratedValue === "true") {
      this.narrationTarget.replaceChildren(
        ...this.buildNarratedHeaderNodes()
      );
    } else {
      this.narrationTarget.replaceChildren(
        this.buildSearchingNode()
      );
    }
  }

  buildNarratedHeaderNodes() {
    const nodes = [];

    nodes.push(
      document.createTextNode("Encontré ")
    );

    const yellow = document.createElement("span");
    yellow.className =
      "inline-block px-2.5 py-0.5 rounded-md " +
      "bg-[#FDE047] align-middle";
    const yellowInner =
      document.createElement("span");
    yellowInner.className = "inline-block";
    yellowInner.dataset.testid = "venue-name-chip";
    yellowInner.textContent = this.venueNameValue;
    yellow.appendChild(yellowInner);
    nodes.push(yellow);

    nodes.push(document.createTextNode(" "));

    const city = document.createElement("span");
    city.className =
      "inline-flex items-baseline gap-1";
    city.dataset.testid = "venue-city-phrase";
    city.textContent = `en ${this.cityValue}.`;
    nodes.push(city);

    return nodes;
  }

  buildSearchingNode() {
    const span = document.createElement("span");
    span.dataset.testid = "locating-searching";
    span.textContent = "Buscando tu restaurante…";
    return span;
  }

  syncStats() {
    this.replaceStat(
      this.hasRatingValueTarget
        ? this.ratingValueTarget
        : null,
      this.ratingValue,
      "stat-reviews-value",
      "value"
    );
    this.replaceStat(
      this.hasRatingMetaTarget
        ? this.ratingMetaTarget
        : null,
      this.reviewsCountValue
        ? `${this.reviewsCountValue} reseñas`
        : "",
      "stat-reviews-meta",
      "meta"
    );
    this.replaceStat(
      this.hasCuisineValueTarget
        ? this.cuisineValueTarget
        : null,
      this.cuisineValue,
      "stat-cuisine-value",
      "value"
    );
    this.replaceStat(
      this.hasCuisineMetaTarget
        ? this.cuisineMetaTarget
        : null,
      this.cuisineMetaValue,
      "stat-cuisine-meta",
      "meta"
    );
    // neighborsCount value is JS-driven by the
    // staggered reveal: don't replace its current
    // text (it's already "0" until pins land).
    this.replaceStat(
      this.hasNeighborsMetaTarget
        ? this.neighborsMetaTarget
        : null,
      this.neighborsMetaValue,
      "stat-neighbors-meta",
      "meta"
    );
    this.replaceStat(
      this.hasWebsiteValueTarget
        ? this.websiteValueTarget
        : null,
      this.websiteValue,
      "stat-website-value",
      "value"
    );
    this.replaceStat(
      this.hasWebsiteMetaTarget
        ? this.websiteMetaTarget
        : null,
      this.websiteHostValue,
      "stat-website-meta",
      "meta"
    );

    // The neighbors counter value element starts as
    // a skeleton wrapper (when not narrated). Once
    // narration lands, swap the wrapper for a real
    // `<span>0</span>` so the stagger has somewhere
    // to write into.
    if (
      this.narratedValue === "true" &&
      this.hasNeighborsCountTarget
    ) {
      const el = this.neighborsCountTarget;
      if (
        el.tagName.toLowerCase() !== "span" ||
        el.dataset.testid ===
          "stat-neighbors-value-skeleton"
      ) {
        const span = this.makeStatSpan(
          this.revealedCompetitors.toString(),
          "stat-neighbors-value",
          "value"
        );
        span.dataset.locatingMapTarget =
          "neighborsCount";
        el.replaceWith(span);
      }
    }
  }

  // Replace a stat target (which may be a skeleton
  // wrapper or a previously-rendered <span>) with a
  // real <span> carrying the new text. Preserves
  // the data-locating-map-target attribute so the
  // controller keeps its handle on the new element.
  replaceStat(el, text, testid, kind) {
    if (!el) return;
    if (!text || text === "") return;

    const target = el.dataset.locatingMapTarget;
    const span = this.makeStatSpan(
      text,
      testid,
      kind
    );
    if (target) {
      span.dataset.locatingMapTarget = target;
    }
    el.replaceWith(span);
  }

  makeStatSpan(text, testid, kind) {
    const span = document.createElement("span");
    if (kind === "value") {
      span.className =
        "text-[22px] leading-7 " +
        "font-semibold " +
        "tracking-[-0.01em] " +
        "text-base-content";
    } else {
      span.className =
        "text-[13px] leading-4 " +
        "text-[#6E6B62] truncate";
    }
    span.dataset.testid = testid;
    span.textContent = text;
    return span;
  }

  // -------- staggered reveal --------

  beginReveal() {
    if (this.revealStarted) return;
    // Stimulus calls value-changed callbacks before
    // `connect()` runs, including when the element
    // first attaches with a non-default value. Even
    // with our `previousValue === undefined` guard,
    // we can land here without `this.map` being
    // initialized (the dataset value can flip before
    // initMap runs in some attach orders). Bail
    // until the map is ready; whichever of
    // connect()/value-changed callback runs second
    // will pick up the reveal.
    if (!this.map) return;
    this.revealStarted = true;

    this.addVenueMarker();
    this.queueCompetitors();
    this.scheduleCompletionEvent();
  }

  addVenueMarker() {
    if (!this.map) return;
    if (!this.geoReady()) return;
    if (!this.venuePinHtmlValue) return;
    const icon = L.divIcon({
      html: this.venuePinHtmlValue,
      className: "calisto-locating-pin",
      iconSize: null,
      iconAnchor: [0, 0],
    });
    L.marker(
      [
        Number(this.venueLatValue),
        Number(this.venueLngValue),
      ],
      {
        icon,
        interactive: false,
        keyboard: false,
        zIndexOffset: 1000,
      }
    ).addTo(this.map);

    this.fitToCurrentMarkers([
      [
        Number(this.venueLatValue),
        Number(this.venueLngValue),
      ],
    ]);
  }

  queueCompetitors() {
    const competitors = this.competitorsValue;
    const html = this.competitorPinHtmlValue;
    let validIndex = 0;
    competitors.forEach((c, originalIndex) => {
      const lat = Number(c.latitude);
      const lng = Number(c.longitude);
      if (
        !Number.isFinite(lat) ||
        !Number.isFinite(lng)
      ) {
        return;
      }
      const pinHtml = html[originalIndex];
      if (!pinHtml) return;
      const delay =
        FIRST_COMPETITOR_DELAY_MS +
        validIndex * COMPETITOR_STAGGER_MS;
      validIndex += 1;
      const timeout = setTimeout(() => {
        this.addCompetitorMarker(c, pinHtml);
      }, delay);
      this.competitorTimeouts.push(timeout);
    });
  }

  addCompetitorMarker(c, pinHtml) {
    const icon = L.divIcon({
      html: pinHtml,
      className: "calisto-locating-pin",
      iconSize: null,
      iconAnchor: [0, 0],
    });
    L.marker(
      [Number(c.latitude), Number(c.longitude)],
      {
        icon,
        interactive: false,
        keyboard: false,
      }
    ).addTo(this.map);

    this.revealedCompetitors += 1;
    this.refitVisibleBounds();
    this.bumpNeighborsCount(
      this.revealedCompetitors
    );
  }

  refitVisibleBounds() {
    const venuePoint = [
      Number(this.venueLatValue),
      Number(this.venueLngValue),
    ];
    const points = [venuePoint];
    this.map.eachLayer((layer) => {
      if (!(layer instanceof L.Marker)) return;
      const ll = layer.getLatLng();
      if (
        ll.lat === venuePoint[0] &&
        ll.lng === venuePoint[1]
      ) {
        return;
      }
      points.push([ll.lat, ll.lng]);
    });
    this.fitToCurrentMarkers(points);
  }

  fitToCurrentMarkers(points) {
    if (!this.map) return;
    if (points.length === 0) return;
    if (points.length === 1) {
      this.map.panTo(points[0], {
        animate: !this.reducedMotion,
      });
      return;
    }
    if (this.reducedMotion) {
      this.map.fitBounds(points, {
        padding: FIT_PADDING,
        paddingBottomRight: [
          FIT_PADDING[0],
          FIT_PADDING_BOTTOM,
        ],
        maxZoom: 16,
        animate: false,
      });
      return;
    }
    this.map.flyToBounds(points, {
      padding: FIT_PADDING,
      paddingBottomRight: [
        FIT_PADDING[0],
        FIT_PADDING_BOTTOM,
      ],
      maxZoom: 16,
      animate: true,
      duration: 0.6,
    });
  }

  bumpNeighborsCount(count) {
    if (!this.hasNeighborsCountTarget) return;
    this.neighborsCountTarget.textContent =
      String(count);
  }

  scheduleCompletionEvent() {
    const valid = this.competitorsValue.filter(
      (c) =>
        Number.isFinite(Number(c.latitude)) &&
        Number.isFinite(Number(c.longitude))
    );
    const lastPinAt =
      valid.length === 0
        ? 0
        : FIRST_COMPETITOR_DELAY_MS +
          (valid.length - 1) *
            COMPETITOR_STAGGER_MS;
    const tail = this.reducedMotion ? 0 : 700;
    const totalDelay = lastPinAt + tail;

    const timeout = setTimeout(() => {
      // Stamp the parent stage element with a
      // completion flag BEFORE dispatching the
      // event. Stimulus controller connect order is
      // non-deterministic: if the locating-map
      // controller connects and fires this event
      // before audit-stage attaches its listener,
      // the event is lost. The flag lets
      // audit-stage's scheduleLocatingAdvance see
      // the completion regardless of attach order.
      const stage = this.element.closest(
        '[data-audit-stage-target="stage"]'
      );
      if (stage) {
        stage.dataset.locatingMapComplete = "true";
        stage.dataset.auditStepComplete = "true";
      }
      this.element.dispatchEvent(
        new CustomEvent("locating-map:complete", {
          bubbles: true,
          detail: {
            competitorsRevealed: valid.length,
          },
        })
      );
      // Step is "ready": Continue button can
      // enable. The button listens on document.
      document.dispatchEvent(
        new CustomEvent("audit-step:complete", {
          detail: {stepKey: "locating"},
        })
      );
    }, totalDelay);
    this.competitorTimeouts.push(timeout);
  }

  // Tolerates an unset list: `teardownMap` runs at the
  // TOP of connect(), before the list is initialised, so
  // that a second connect cancels the reveal the first
  // one scheduled instead of dropping the handles on the
  // floor.
  clearCompetitorTimeouts() {
    (this.competitorTimeouts || []).forEach((id) => {
      clearTimeout(id);
    });
    this.competitorTimeouts = [];
  }
}
